/**
 * LanguageSwitcher Component
 *
 * Toggle button for switching between Swedish and English languages.
 * Updates URL and localStorage. No React Context dependency.
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { useState, useEffect } from 'react';
import type { Locale } from '@/utils/languageDetection';

export function LanguageSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentLocale, setCurrentLocale] = useState<Locale>('sv');

  // Detect locale from URL path
  useEffect(() => {
    const locale: Locale = location.pathname.startsWith('/en') ? 'en' : 'sv';
    setCurrentLocale(locale);

    // Update localStorage
    try {
      localStorage.setItem('ssn-locale', locale);
    } catch (error) {
      console.error('Error saving locale:', error);
    }
  }, [location.pathname]);

  /**
   * Handle language switch
   * Navigates to equivalent page in target language
   */
  const handleLanguageSwitch = () => {
    const newLocale: Locale = currentLocale === 'en' ? 'sv' : 'en';

    // Strip current locale prefix and add new one
    const currentPath = location.pathname.replace(/^\/(en|sv)/, '') || '/';
    const newPath = `/${newLocale}${currentPath}`;

    navigate(newPath, { replace: true });
  };

  // Determine ARIA label based on current locale
  const ariaLabel = currentLocale === 'en'
    ? 'Byt till svenska'
    : 'Switch to English';

  return (
    <button
      onClick={handleLanguageSwitch}
      className="inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors
                 bg-gray-800 text-gray-100 hover:bg-gray-700
                 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-electric-cyan
                 min-w-[44px] min-h-[44px]"
      aria-label={ariaLabel}
      title={ariaLabel}
      type="button"
    >
      <Globe className="h-4 w-4" aria-hidden="true" />
      <span className="font-semibold uppercase">
        {currentLocale === 'en' ? 'SV' : 'EN'}
      </span>
    </button>
  );
}

export default LanguageSwitcher;
